import React from "react";
import { BaseModal } from "../../../../atoms/base-modal";
import { CsvUploader } from "../../../../molecules/csv-uploader";
import { matchType } from "../../../../../application/domain/base/base-type";
import {
  convertErrorCode,
  UpdateProcedureGroupPreviewResponse,
  UpdateProcedureGroupResponse,
} from "../../../../../application/domain/procedures/procedure-update-group";
import { useUpdateProcedureGroup } from "./use-update-procedure-group";

import { toast } from "react-toastify";
import {
  PrimaryButton,
  SecondaryButton,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
} from "@graffer-inc/console-design-kit";

export const UpdateProcedureGroupModal: React.FC<{
  isActive: boolean;
  onClose: () => void;
  orgId?: string;
  downloadCsvForUpdateGroup: (
    orgId: string,
    groupIds: string[],
  ) => Promise<void | string>;
  bulkUpdateGroupPreview: (
    csv: string,
    orgId: string,
  ) => Promise<UpdateProcedureGroupPreviewResponse | string>;
  bulkUpdateGroup: (
    csv: string,
    orgId: string,
  ) => Promise<UpdateProcedureGroupResponse | string>;
}> = ({
  isActive,
  onClose,
  orgId = "",
  downloadCsvForUpdateGroup,
  bulkUpdateGroupPreview,
  bulkUpdateGroup,
}) => {
  const {
    operationStatus,
    setOperationStatus,
    updatePreviewResponse,
    updateResponse,
    file,
    setFile,
    execPreview,
    execBulkUpdate,
    isExistValidateError,
  } = useUpdateProcedureGroup({
    bulkUpdateGroupPreview,
    bulkUpdateGroup,
    orgId,
  });

  return (
    <BaseModal
      title={matchType(operationStatus, {
        DOWNLOAD: "管理グループの一括更新",
        PREVIEW: "更新内容の確認",
        RESULT: "一括更新の結果",
      })}
      isActive={isActive}
      onClose={onClose}
      hasOverflow={false}
    >
      {matchType(operationStatus, {
        DOWNLOAD: (
          <>
            <div className="modal-card-body">
              <div className="mb-5">
                <p className="mb-2">
                  更新用CSVをダウンロードし、管理グループを変更したい手続きに
                  <strong>管理グループID</strong>を記載してください。
                </p>
                <SecondaryButton
                  text={"更新用CSVをダウンロード"}
                  onClick={async () => {
                    try {
                      await downloadCsvForUpdateGroup(orgId, []);
                    } catch (e) {
                      toast.error(e);
                    }
                  }}
                  icon={"DownloadIcon"}
                />
              </div>
              <div>
                <CsvUploader file={file} setFile={setFile} />
                <p>
                  更新したCSVをアップロードしたら、内容の確認に進んでください。
                </p>
              </div>
            </div>
            <footer className="modal-card-foot">
              <SecondaryButton
                text="キャンセル"
                onClick={onClose}
                style={{ flexGrow: 1 }}
              />
              <PrimaryButton
                onClick={execPreview}
                text="内容の確認に進む"
                style={{ flexGrow: 1 }}
              />
            </footer>
          </>
        ),
        PREVIEW: (
          <>
            {updatePreviewResponse && (
              <>
                <div className="modal-card-body">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <Th>手続きID</Th>
                        <Th>手続き名称</Th>
                        <Th>管理グループID</Th>
                        <Th>警告/エラー</Th>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {updatePreviewResponse.results.map((r, index) => (
                        <TableRow key={index}>
                          <Td>{r.csvRow.procedureId}</Td>
                          <Td>{r.csvRow.procedureName}</Td>
                          <Td>{r.csvRow.groupId}</Td>
                          <Td>
                            {r.validateError?.errCode && (
                              <p>{convertErrorCode(r.validateError.errCode)}</p>
                            )}
                          </Td>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {isExistValidateError && (
                    <p className="mt-4">
                      手続き情報が正しくないため、一括更新を実行できません。警告/エラーの項目を確認してください。
                    </p>
                  )}
                </div>
                <footer className="modal-card-foot">
                  <SecondaryButton
                    text="戻る"
                    onClick={() => {
                      setOperationStatus("DOWNLOAD");
                    }}
                    style={{ flexGrow: 1 }}
                  />
                  <PrimaryButton
                    onClick={execBulkUpdate}
                    text="一括更新"
                    style={{ flexGrow: 1 }}
                  />
                </footer>
              </>
            )}
          </>
        ),
        RESULT: (
          <>
            {updateResponse && (
              <>
                <div className="modal-card-body">
                  {updateResponse.count === 0 ? (
                    <p>更新件数は0件でした。</p>
                  ) : (
                    <p>
                      CSVに差分があった{updateResponse.count}件を更新しました。
                    </p>
                  )}
                </div>
                <footer className="modal-card-foot">
                  <SecondaryButton
                    text="閉じる"
                    onClick={onClose}
                    style={{ flexGrow: 1 }}
                  />
                </footer>
              </>
            )}
          </>
        ),
      })}
    </BaseModal>
  );
};
